import React from 'react'

import { CopyLinkInput } from '@components/common/CopyLinkInput'
import { Suggestion } from '@components/common/Suggestion'
import { ButtonClean } from '@fourthwall/components'

import type { Recommendation } from '../../../Recommendations.types'
import { useRecommendationsDismiss } from '../../../hooks/useRecommendationsDismiss'
import { useReferralConfigurationQuery } from '../../../queries/useReferralConfigurationQuery'
import referAFriend from '../assets/refer-a-friend.png'
import { AFFILIATE_LEARN_MORE_LINK, ANALYTICS, MAX_REDEMPTIONS_TITLE_THRESHOLD } from '../consts'
import { getAffiliateDescription } from '../utils/getAffiliateDescription'
import { getReferralDescription } from '../utils/getReferralDescription'

export const useReferralRecommendation = () => {
  const { referralConfigurationQuery } = useReferralConfigurationQuery([{ pathOverride: 'join' }], {
    onError: undefined,
  })
  const { createOnDismiss, recommendationsDismiss } = useRecommendationsDismiss()
  const link = referralConfigurationQuery.data?.shortLink
  const maxRedemptions = referralConfigurationQuery.data?.referralConfiguration.maxRedemptions || 0
  const hasMaxRedemptions = maxRedemptions && maxRedemptions >= 0
  const redeemedTimes = referralConfigurationQuery.data?.redeemedSummary.redeemedTimes || 0
  const isMaxRedemptionsReached = hasMaxRedemptions ? redeemedTimes >= maxRedemptions : false
  const affiliateType = referralConfigurationQuery.data?.affiliateType

  const getTitle = () => {
    if (affiliateType) {
      return 'Refer a friend'
    }

    // -1 means unlimited. Used as a stopgap because null breaking mobile.
    if (hasMaxRedemptions && maxRedemptions <= MAX_REDEMPTIONS_TITLE_THRESHOLD) {
      return `Refer up to ${maxRedemptions} of your friends`
    }

    return 'Refer a friend'
  }

  const getDescription = () => {
    if (affiliateType) {
      return (
        <p>
          {getAffiliateDescription(affiliateType).description}{' '}
          <ButtonClean href={AFFILIATE_LEARN_MORE_LINK} target="_blank" label="Learn more" />
        </p>
      )
    }

    if (!referralConfigurationQuery.data?.referralConfiguration) return ''

    return getReferralDescription(referralConfigurationQuery.data?.referralConfiguration)
  }

  return {
    shortLink: referralConfigurationQuery.data?.shortLink,
    recommendationTitle: getTitle(),
    recommendationDescription: getDescription(),
    referralRecommendation: {
      id: 'referral',
      enabled: !!link && !isMaxRedemptionsReached,

      isLoading: referralConfigurationQuery.isInitialLoading,
      suggestion: (
        <Suggestion
          title={getTitle()}
          description={getDescription()}
          imageUrl={referAFriend}
          analytics={ANALYTICS}
          content={<CopyLinkInput href={link} analytics={ANALYTICS} />}
          onDismiss={createOnDismiss(recommendationsDismiss.referral.dismiss)}
        />
      ),
    } satisfies Recommendation,
  }
}
